import type {
  FetchPersonalProfileVersionForJobFn,
  FetchSpousalProfileVersionForJobFn,
  FetchDependentProfileVersionForJobFn,
  PersonalProfileEditorViewModelFactory,
  SpousalProfileEditorViewModelFactory,
  DependentProfileEditorViewModelFactory,
  TaxProfileFeatureOptions,
  ListDependentsFn,
  PromptDeleteDependentFn,
  TaxRefundMethodEditorViewModelFactory,
  FetchPersonalTaxRefundMethodForJobFn,
  BusinessProfileEditorViewModelFactory,
  FetchBusinessProfileForJobFn,
  FetchBusinessTaxRefundMethodForJobFn,
  ListBusinessProfilesFn,
} from '@/features/tax-profile'
import { PersonalProfileEditorViewModel } from './individual-profiles/PersonalProfileEditorViewModel'
import { SpousalProfileEditorViewModel } from './individual-profiles/SpousalProfileEditorViewModel'
import { DependentProfileEditorViewModel } from './individual-profiles/DependentProfileEditorViewModel'
import { IndividualProfileStore } from '@/features/tax-profile/individual-profiles/IndividualProfileStore'
import { TaxProfileApi } from '@/features/tax-profile/api/tax-profile-api'
import { DeleteDependentDialogViewModel } from '@/features/tax-profile/individual-profiles/components/DeleteDependentDialog/DeleteDependentDialogViewModel'
import Env from '@/infra/env'
import { TaxRefundMethodStore } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethodStore'
import type { ListTaxRefundMethodsQuery } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethodStore'
import { TaxRefundMethodEditorViewModel } from './tax-refund-methods/TaxRefundMethodEditorViewModel'
import type { TaxRefundMethodVersion } from './tax-refund-methods/TaxRefundMethod'
import { DeleteTaxRefundMethodDialogViewModel } from '@/features/tax-profile/tax-refund-methods/components/DeleteTaxRefundMethodDialog/DeleteTaxRefundMethodDialogViewModel'
import { BusinessProfileStore } from '@/features/tax-profile/business-profiles/BusinessProfileStore'
import { BusinessProfileEditorViewModel } from '@/features/tax-profile/business-profiles/BusinessProfileEditorViewModel'
import { DeleteBusinessProfileDialogViewModel } from '@/features/tax-profile/business-profiles/DeleteBusinessProfileDialogViewModel'
import type { BusinessProfileVersion } from '@/features/tax-profile/business-profiles/BusinessProfile'

export function InternalTaxProfileFeature(options: TaxProfileFeatureOptions) {
  const api = new TaxProfileApi({
    token$: options.token$,
    baseURL: Env.WORK_DOTNET_API,
  })

  const individualProfileStore = new IndividualProfileStore(
    api,
    options.getCurrentMember,
  )

  const taxRefundMethodStore = new TaxRefundMethodStore(
    api,
    options.getCurrentMember,
  )

  const businessProfileStore = new BusinessProfileStore(
    api,
    options.getCurrentMember,
  )

  const deleteDependentDialogViewModel = new DeleteDependentDialogViewModel(
    options.createSnackbarErrorHandler,
    individualProfileStore,
  )

  const deleteTaxRefundMethodDialogViewModel =
    new DeleteTaxRefundMethodDialogViewModel(
      options.createSnackbarErrorHandler,
      taxRefundMethodStore,
    )

  const deleteBusinessProfileDialogViewModel =
    new DeleteBusinessProfileDialogViewModel(
      options.createSnackbarErrorHandler,
      businessProfileStore,
    )

  const createPersonalProfileEditorViewModel: PersonalProfileEditorViewModelFactory =
    (vmOptions) =>
      new PersonalProfileEditorViewModel(
        options.getCurrentMember,
        individualProfileStore,
        options.createSnackbarErrorHandler,
        vmOptions,
      )

  const createSpousalProfileEditorViewModel: SpousalProfileEditorViewModelFactory =
    (vmOptions) =>
      new SpousalProfileEditorViewModel(
        individualProfileStore,
        options.createSnackbarErrorHandler,
        vmOptions,
      )

  const createDependentProfileEditorViewModel: DependentProfileEditorViewModelFactory =
    (vmOptions) =>
      new DependentProfileEditorViewModel(
        individualProfileStore,
        options.createSnackbarErrorHandler,
        vmOptions,
      )

  const createTaxRefundMethodEditorViewModel: TaxRefundMethodEditorViewModelFactory =
    (vmOptions) =>
      new TaxRefundMethodEditorViewModel(
        taxRefundMethodStore,
        options.createSnackbarErrorHandler,
        vmOptions,
      )

  const createBusinessProfileEditorViewModel: BusinessProfileEditorViewModelFactory =
    (vmOptions) =>
      new BusinessProfileEditorViewModel(
        options.getCurrentMember,
        businessProfileStore,
        options.createSnackbarErrorHandler,
        vmOptions,
      )

  const fetchPersonalProfileVersionForJob: FetchPersonalProfileVersionForJobFn =
    (jobId, signal) =>
      individualProfileStore.fetchPersonalProfileVersionForJob(jobId, signal)

  const fetchSpousalProfileVersionForJob: FetchSpousalProfileVersionForJobFn = (
    jobId,
    signal,
  ) => individualProfileStore.fetchSpousalProfileVersionForJob(jobId, signal)

  const fetchDependentProfileVersionForJob: FetchDependentProfileVersionForJobFn =
    (jobId, profileId, signal) =>
      individualProfileStore.fetchDependentProfileVersionForJob(
        jobId,
        profileId,
        signal,
      )

  const fetchPersonalTaxRefundMethodForJob: FetchPersonalTaxRefundMethodForJobFn =
    (jobId, signal) =>
      taxRefundMethodStore.fetchPersonalTaxRefundMethodForJob(jobId, signal)

  const listDependents: ListDependentsFn = (query, signal) =>
    individualProfileStore.listDependents(query, signal)

  const promptDeleteDependent: PromptDeleteDependentFn = (dependent, signal) =>
    deleteDependentDialogViewModel.prompt(dependent, signal)

  const listTaxRefundMethods = (
    query: ListTaxRefundMethodsQuery,
    signal?: AbortSignal,
  ) => taxRefundMethodStore.listTaxRefundMethods(query, signal)

  const promptDeleteTaxRefundMethod = (
    method: TaxRefundMethodVersion,
    signal?: AbortSignal,
  ) => deleteTaxRefundMethodDialogViewModel.prompt(method, signal)

  const fetchBusinessProfileForJob: FetchBusinessProfileForJobFn = (
    jobId,
    signal,
  ) => businessProfileStore.fetchBusinessProfileForJob(jobId, signal)

  const fetchBusinessTaxRefundMethodForJob: FetchBusinessTaxRefundMethodForJobFn =
    (jobId, signal) =>
      taxRefundMethodStore.fetchBusinessTaxRefundMethodForJob(jobId, signal)

  const listBusinessProfiles: ListBusinessProfilesFn = (query, signal) =>
    businessProfileStore.listBusinessProfiles(query, signal)

  const promptDeleteBusinessProfile = (
    profile: BusinessProfileVersion,
    signal?: AbortSignal,
  ) => deleteBusinessProfileDialogViewModel.prompt(profile, signal)

  return {
    // Internal
    individualProfileStore,
    deleteDependentDialogViewModel,
    taxRefundMethodStore,
    deleteTaxRefundMethodDialogViewModel,
    businessProfileStore,
    deleteBusinessProfileDialogViewModel,

    // Public
    createPersonalProfileEditorViewModel,
    createSpousalProfileEditorViewModel,
    createDependentProfileEditorViewModel,
    createTaxRefundMethodEditorViewModel,
    createBusinessProfileEditorViewModel,
    fetchPersonalProfileVersionForJob,
    fetchSpousalProfileVersionForJob,
    fetchDependentProfileVersionForJob,
    fetchBusinessProfileForJob,
    fetchPersonalTaxRefundMethodForJob,
    fetchBusinessTaxRefundMethodForJob,
    listDependents,
    listBusinessProfiles,
    promptDeleteDependent,
    listTaxRefundMethods,
    promptDeleteTaxRefundMethod,
    promptDeleteBusinessProfile,
  }
}
